import { useCallback, useId, useLayoutEffect, useRef } from 'react';
import { Rectangle } from './Rectangle';
import { QuadTree } from './QuadTree';
import { Point } from './Point';
import { createNoise2D, NoiseFunction2D } from 'simplex-noise';

const MOVE_SPEED = 0.05;
const POINT_SIZE = 1.2;
const TOTAL_POINTS = (window.innerWidth + window.innerHeight) / 12;
//const TOTAL_POINTS = 500;
const QUADTREE_CAPACITY = 12;

const DISTANCE_TO_START_DRAWING_LINES =
  (window.innerWidth + window.innerHeight) / 30;

export const AnimatedBackground = ({
  width,
  height,
  pause,
}: {
  width: number;
  height: number;
  pause: boolean;
}) => {
  const id = useId();

  const noiseRef = useRef<NoiseFunction2D>(createNoise2D());

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>();

  const startTime = useRef<number>(0);
  const opacity = useRef<number>(1);

  const quadtree = useRef<QuadTree | null>(null);
  const pointList = useRef<Point[]>([]);

  const draw = useCallback(
    (timestamp: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !quadtree.current) return;

      if (!startTime.current) startTime.current = timestamp;
      const elapsedTime = timestamp - startTime.current;
      const maxDuration = 0;
      opacity.current = Math.min(1, elapsedTime / maxDuration);

      const ctx = canvas.getContext('2d')!;

      ctx.globalAlpha = Number.isNaN(opacity.current) ? 0 : opacity.current;

      ctx.fillStyle = '#02172C';
      ctx.fillRect(0, 0, width, height);

      // quadtree.current!.draw(ctx);
      pointList.current.forEach(currentPoint => {
        currentPoint.draw(ctx);
        currentPoint.connect(DISTANCE_TO_START_DRAWING_LINES);
        currentPoint.move();
      });

      if (!pause) {
        animationFrameId.current = requestAnimationFrame(draw);
      } else {
        console.log('paused');
      }
    },
    [width, height, pause, quadtree.current],
  );

  useLayoutEffect(() => {
    animationFrameId.current = requestAnimationFrame(draw);

    const canvas = canvasRef.current!;
    canvas.width = width;
    canvas.height = height;

    const screen = new Rectangle(0, 0, width, height);

    const boundary = new Rectangle(
      width / 2,
      height / 2,
      width / 2,
      height / 2,
    );
    quadtree.current = new QuadTree(boundary, QUADTREE_CAPACITY, null);

    // Create random Points all over the screen
    for (let i = 0; i < TOTAL_POINTS; i++) {
      const p = new Point(
        noiseRef.current,
        screen,
        width,
        height,
        MOVE_SPEED,
        POINT_SIZE,
        quadtree.current,
      );
      pointList.current.push(p);
      quadtree.current.insert(p);
    }

    console.log('BG RENDERED');

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      pointList.current.length = 0;
      quadtree.current = null;
    };
  }, [pause, width, height, draw]);

  return (
    <canvas
      ref={canvasRef}
      id={id}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: -1,
        backgroundColor: 'var(--tc-color-brand-secondary)',
        width: '100vw',
        height: '100vh',
      }}
    />
  );
};
